import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  arrayUnion,
  Timestamp
} from "firebase/firestore";

import { db } from "../../../firebase";

import {
  generatePONumber
} from "./poNumberService";

// D-291 (P3c): every PO line is checked by checkPOLines() before a PO is
// created or amended; the ordered value comes ONLY from computeOrderValue();
// a vendor's GSTIN / registration status ONLY from gstBill.js.
import { computeOrderValue, vendorGstinOf, vendorGstStatus } from "../../../utils/gstBill";
import { checkPOLines } from "../../../utils/poLines";
import { stockRateFromPOLine } from "../../../utils/grnPlan";
// HIGH-01: who may approve / amend / reassign - the shared lists (the rules mirror them).
import { PO_APPROVER_ROLES, PO_AMEND_ROLES, PO_REASSIGN_ROLES, hasPoRole } from "../../../utils/poRoles";

/*

  RATE BREAKDOWN — LANDED COST (REFERENCE ONLY)

  Never touches `rate` — that field is GRN's own, entered fresh at GRN
  time per CORE_RULES §6 ("Rate saved at PO time is FORBIDDEN — rate at
  GRN time only"). base_rate/discount/freight/GST here are a SEPARATE,
  additive reference figure shown on PO / Vendor Assignment screens so a
  Manager can judge true cost across vendors — comparing base rate alone
  is misleading when discount/freight/GST differ between vendors.

  Freight is entered ONCE per PO (one shipment cost) and split across
  items proportionally by each item's post-discount subtotal.

*/

export const calculateItemDiscount = (baseRate, qty, discountType, discountValue) => {
  const subtotal = Number(baseRate || 0) * Number(qty || 0);
  const value    = Number(discountValue || 0);
  if (discountType === "percent") {
    return subtotal * (value / 100);
  }
  // "flat" — a flat ₹ discount is a total for this line, not per-unit
  return Math.min(value, subtotal); // never let discount exceed the subtotal
};

/*

  LOWEST-RATE SUGGESTION — cross-vendor rate history (reference only)

  Reads the SAME base_rate/discount/gst/landed_rate fields Rate Breakdown
  already writes on every purchase_orders doc — no new collection, no new
  write path. Company-scoped, last 12 months, fetched ONCE per page load
  (same pattern as itemsMaster) and matched client-side per item, instead
  of a fresh query per item selection — avoids the N+1 anti-pattern this
  codebase already guards against elsewhere (ProcurementQueue etc.).

  NOTE: POs created before the Rate Breakdown feature existed will show
  landed_rate === base_rate (no discount/freight/GST was ever captured for
  them) — still a useful plain-rate reference, just not a true landed
  figure. Not flagged specially; the date next to each entry makes this
  self-evident to whoever's reading it.

*/

export const fetchRecentPOHistory = async (company_id) => {
  if (!company_id) return [];
  try {
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    const snap = await getDocs(query(
      collection(db, "purchase_orders"),
      where("company_id", "==", company_id),
      orderBy("created_at", "desc"),
      limit(300) // pragmatic cap — revisit if a company's PO volume grows well past this
    ));

    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(po => {
        if (!po.created_at) return false;
        const createdDate = po.created_at instanceof Timestamp ? po.created_at.toDate() : new Date(po.created_at);
        return createdDate >= twelveMonthsAgo;
      });
  } catch (error) {
    console.error("[fetchRecentPOHistory] failed:", error);
    return []; // fail soft — the suggestion feature is a nice-to-have, never blocks PO creation
  }
};

// Returns up to 5 historical (vendor, rate, date) entries for one item,
// sorted by landed_rate ascending (cheapest first).
// D-304 B: an Item Master item is matched by item_id ONLY — never by name
// (names repeat: "Copper pipe 15.9mm" vs "19.1mm" must never share a rate
// history). Only a custom item (no Item Master id) falls back to an exact
// name match, and only against other custom lines.
export const getItemRateHistory = (poHistory, item) => {
  if (!item?.item_name?.trim()) return [];
  const targetName = item.item_name.toLowerCase().trim().replace(/\s+/g, " ");
  const targetId   = String(item.item_id || "").trim();
  const isCustom   = !!item.is_custom_item || !targetId || targetId.startsWith("custom_");

  const entries = [];
  for (const po of poHistory) {
    for (const poItem of (po.items || [])) {
      if (poItem._cancelled) continue;
      const lineId     = String(poItem.item_id || "").trim();
      const lineCustom = !!poItem.is_custom_item || !lineId || lineId.startsWith("custom_");
      const matches = isCustom
        ? lineCustom && (poItem.item_name || "").toLowerCase().trim().replace(/\s+/g, " ") === targetName
        : !lineCustom && lineId === targetId;
      if (!matches) continue;

      entries.push({
        po_number:   po.po_number || po.id,
        vendor_name: po.vendor_name || "Unknown vendor",
        created_at:  po.created_at,
        base_rate:   Number(poItem.base_rate || poItem.rate || 0),
        landed_rate: Number(poItem.landed_rate || poItem.base_rate || poItem.rate || 0),
      });
    }
  }

  return entries
    .filter(e => e.landed_rate > 0)
    .sort((a, b) => a.landed_rate - b.landed_rate)
    .slice(0, 5);
};

export const calculateLandedCost = (items = [], freightAmount = 0) => {
  const freight = Number(freightAmount || 0);

  // Pass 1 — post-discount subtotal per item (needed to proportion freight)
  const afterDiscount = items.map((item) => {
    const qty      = Number(item.qty || item.ordered_qty || 0);
    const subtotal = Number(item.base_rate || 0) * qty;
    const discount = calculateItemDiscount(item.base_rate, qty, item.discount_type, item.discount_value);
    return Math.max(subtotal - discount, 0);
  });

  const totalAfterDiscount = afterDiscount.reduce((s, v) => s + v, 0);

  // Pass 2 — freight share + GST → landed total + landed rate/unit
  return items.map((item, idx) => {
    const qty               = Number(item.qty || item.ordered_qty || 0);
    const itemAfterDiscount = afterDiscount[idx];
    const freightShare      = totalAfterDiscount > 0
      ? (itemAfterDiscount / totalAfterDiscount) * freight
      : 0;
    const gstPercent        = Number(item.gst_percent || 0);
    const landedSubtotal    = itemAfterDiscount + freightShare;
    const landedTotal       = landedSubtotal * (1 + gstPercent / 100);
    const landedRate        = qty > 0 ? landedTotal / qty : 0;
    const baseSubtotal      = Number(item.base_rate || 0) * qty;

    return {
      ...item,
      discount_amount:  Number((baseSubtotal - itemAfterDiscount).toFixed(2)),
      freight_share:    Number(freightShare.toFixed(2)),
      landed_subtotal:  Number(landedSubtotal.toFixed(2)),
      landed_total:     Number(landedTotal.toFixed(2)),
      landed_rate:      Number(landedRate.toFixed(2)),
    };
  });
};

/*

  CREATE PURCHASE ORDER

*/

/*
  GRN / FINALIZE RATE PREFILL (D-304 Phase C)
  D-291 P3d-4: the GRN and Direct Site finalize screens now call
  stockRateFromPOLine(line, { itcClaimed }) in src/utils/grnPlan.js - rate
  before GST when the company claims ITC, incl. GST when it does not (the
  vendor payable now comes from the purchase bill, not the GRN rate).
  This GST-inclusive version is kept only for any old caller; it is exactly
  stockRateFromPOLine with itcClaimed false (proven in grnPlan.test.js).
*/
export const grnRateFromPOLine = (line = {}) => stockRateFromPOLine(line, { itcClaimed: false });

// Builds the purchase_orders document (no writes). Shared by
// createPurchaseOrder() (Direct PO) and createPurchaseOrderFromRequest()
// (Procurement Queue → PR → PO) so both store exactly the same shape.
// Exported ONLY for the Jest key test (HIGH-01: its keys must equal
// PO_CREATE_KEYS = the create rule) - never write a PO with it elsewhere.
export const buildPurchaseOrderDoc = (payload, poNumber) => {

      // NORMALIZE ITEMS

      const normalizedItems =

  (payload.items || []).map(
    (item, index) => {

      const orderedQty =
        Number(
          item.qty ||
          item.ordered_qty ||
          0
        );

      // CUSTOM ITEM SUPPORT — item_id missing/blank means it's a custom
      // (hand-typed, not in Item Master) item. A stable placeholder ID is
      // generated HERE ONLY — single source, no other file duplicates this
      // logic. is_custom_item carries through so GRN can apply the correct
      // rule: custom items are received for record-keeping only, never
      // matched to Item Master, never credited to inventory_summary
      // (Constitution §5 / CORE_RULES §6).
      const isCustom =
        !!item.is_custom_item || !item.item_id;

      const resolvedItemId =
        item.item_id ||
        `custom_${Date.now()}_${index}`;

      return {

        // LINE TRACKING

        line_id:
          `${resolvedItemId}_${index}`,

        // MATERIAL

        item_id:
          resolvedItemId,

        item_name:
          item.item_name || "",

        item_code:
          item.item_code || "",

        item_category:
          item.item_category || "",

        is_custom_item:
          isCustom,

        // QUANTITY

        qty: orderedQty,

        ordered_qty:
          orderedQty,

        received_qty: 0,

        pending_qty:
          orderedQty,

        rejected_qty: 0,

        // RECEIVE STATUS

        item_receive_status:
          "pending",

        item_status:
          "pending",

        // UNIT

        unit:
          item.unit ||
          item.uom ||
          "nos",

        // FINANCIALS

        // D-291 P3c — lines were checked by checkPOLines() first.
        // base_rate = the rate agreed with the vendor, BEFORE GST and before
        // the line's discount. `rate` starts as the same value, so a PO never
        // shows Rs. 0 for an agreed rate; the store GRN later overwrites
        // `rate` with the GRN (stock) rate. "Rate not agreed yet" -> both 0.
        rate:
          Number(item.base_rate || 0),

        base_rate:
          Number(item.base_rate || 0),

        rate_not_agreed:
          item.rate_not_agreed === true,

        hsn_code:
          item.hsn_code || "",

        discount_type:
          item.discount_type === "flat" ? "flat" : "percent",

        discount_value:
          Number(item.discount_value || 0),

        // Never "|| 0": an empty GST box was refused by checkPOLines().
        // D-325: null = decided on the vendor's bill ("Rate not agreed yet"
        // lines only). Never Number(null) - that is 0.
        gst_percent:
          item.gst_percent === null || item.gst_percent === undefined
            ? null
            : Number(item.gst_percent),

        amount:
          Number(item.amount || 0),

        // D-304 B: which MRF lines this PO line is for — [{ mrf_id, mrf_no,
        // item_index, site_id, site_name, qty }]. Empty for Direct POs and
        // stock top-ups. Kept so GRN / issue can be traced back to the MRF.
        sources:
          Array.isArray(item.sources) ? item.sources : []

      };
    }
  );

      // RATE BREAKDOWN — compute landed cost per item (reference only)
      const freightAmount =
        Number(payload.freight_amount || 0);

      const itemsWithLandedCost =
        calculateLandedCost(normalizedItems, freightAmount);

      // CALCULATIONS

      const subtotal =

        normalizedItems.reduce(

          (sum, item) =>

            sum + item.amount,

          0
        );

      // Reference-only total — Base Rate × Qty, minus discount, plus
      // proportioned freight, plus GST. NEVER used for GRN/grand_total —
      // that stays received_qty × rate, set fresh at GRN time.
      const landedGrandTotal =
        itemsWithLandedCost.reduce(
          (sum, item) => sum + (item.landed_total || 0),
          0
        );

      // FINAL DOCUMENT

      const purchaseOrderDoc = {

  // MULTI TENANT

  company_id:
    payload.company_id,

        // PURCHASE MODE (Priority 4, Direct Site Purchase)
        // "stock_purchase" (default) → unchanged existing flow.
        // "direct_site_purchase" → vendor delivers to a project site;
        //   Site Engineer confirms qty, Manager/SM/Owner finalizes
        //   with rate; only site_inventory_summary is credited,
        //   central inventory_summary is NEVER touched.
        purchase_mode:
          payload.purchase_mode === "direct_site_purchase"
            ? "direct_site_purchase"
            : "stock_purchase",

        project_id:
          payload.project_id || "",

        project_name:
          payload.project_name || "",

        site_id:
          payload.site_id || "",

        site_name:
          payload.site_name || "",

        // Two-stage site confirmation — only meaningful for
        // direct_site_purchase; stock_purchase POs stay
        // "not_applicable" forever.
        site_confirmation_status:
          payload.purchase_mode === "direct_site_purchase"
            ? "pending_site_confirmation"
            : "not_applicable",

        site_confirmed_items:   [],
        site_confirmed_by:      "",
        site_confirmed_by_name: "",
        site_confirmed_at:      null,

        // Per-PO Site Engineer override (Priority 4 refinement) — empty
        // array means "fall back to the project's whole assigned team"
        // (existing behaviour). Non-empty means ONLY these specific
        // engineers can see/confirm THIS PO, even if the project has
        // others assigned too. Lets a Manager pin a delivery to whoever
        // is actually on-site right now, without touching the project's
        // broader site_engineer_ids[] assignment.
        assigned_site_engineer_ids:
          payload.purchase_mode === "direct_site_purchase"
            ? (payload.assigned_site_engineer_ids || [])
            : [],

        // IDENTIFICATION

        po_number: poNumber,

        po_status: "draft",

        approval_status: "pending",

        receive_status: "pending",

        // payment_status removed (D-289) — a PO's payment state is CALCULATED from the vendor khaata, never stored.

        // TIMESTAMPS

        created_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),

        // VENDOR

        vendor_id:
          payload.vendor_id || "",

        vendor_name:
          payload.vendor_name || "",

        vendor_email:
          payload.vendor_email || "",

        vendor_phone:
          payload.vendor_phone || "",

        vendor_address:
          payload.vendor_address || "",

        vendor_gst:
          payload.vendor_gst || "",

        // D-291 P3c: true = valid GSTIN, false = Unregistered (no GST on
        // the lines), null = not entered yet. Read from the vendor doc.
        vendor_gst_registered:
          payload.vendor_gst_registered ?? null,

        // PROCUREMENT SOURCE

        source_purchase_request_id:

          payload
            .source_purchase_request_id || "",

        // source_type stays "procurement_queue" by default (unchanged
        // behavior for VendorAssignmentModal, which never passes either
        // field) — but now driven by payload so a Direct PO doesn't get
        // silently mislabeled as procurement_queue-originated.
        source_type:
          payload.source_type || payload.source || "procurement_queue",

        // Additive tag, separate from source_type. CreateDirectPO.js passes
        // source:"direct" — any UI reading po.source === "direct" keeps
        // working after consolidation.
        source:
          payload.source || "",

        // GRN / RECEIPT STATE FLAGS — additive, same defaults
        // CreateDirectPO.js's own PO doc previously set directly.
        grn_locked:
          false,

        over_received:
          false,

        short_closed:
          false,

        // DELIVERY

        delivery_date:
          payload.delivery_date || "",

        priority:
          payload.priority || "normal",

        transport:
          payload.transport || "",

        remarks:
          payload.remarks || "",

        // Additive — separate from `remarks`; CreateDirectPO.js's own
        // "Notes" field maps here.
        notes:
          payload.notes || "",

        // AUDIT — additive. Callers that don't pass these get "" exactly
        // as before (created_by/created_by_name were never set at all
        // previously by this function — this only adds them).
        created_by:
          payload.created_by || "",

        created_by_name:
          payload.created_by_name || "",

        updated_by:
          payload.created_by || "",

        // FINANCIALS

        subtotal,

        gst_total: 0,

        discount_total: 0,

        // grand_total = the RECEIVED value, written by the GRN (0 until then).
        // The ORDERED value is calculated with computeOrderValue() and never
        // stored here - the Tally export and the purchase reports read
        // grand_total, so a Draft PO must stay out of them (D-291 P3c, MED-34).
        grand_total: 0,

        // RATE BREAKDOWN — reference only, never drives GRN/grand_total
        freight_amount:
          freightAmount,

        landed_grand_total:
          Number(landedGrandTotal.toFixed(2)),

        // TRACKING

        total_items:
          normalizedItems.length,

        received_items: 0,

        pending_items:
          normalizedItems.length,

        completion_percentage: 0,

        // ITEMS

        items: itemsWithLandedCost

      };

      return purchaseOrderDoc;
};

/*
  PREPARE A NEW PO (D-291 P3c)
  WHY: runs BEFORE a PO number is reserved, so a refused PO never uses up a
  number. Reads the vendor fresh (never trusts the screen's copy - Quick
  Create vendors and old `gst` fields included), and checks every line with
  checkPOLines(). Throws with a message the screen shows as it is.
*/
const gstRegisteredFrom = (status) =>
  status === "registered" ? true : status === "unregistered" ? false : null;

const preparePOPayload = async (payload) => {
  const companyId = String(payload?.company_id || "").trim();
  if (!companyId) throw new Error("company_id required");
  if (!payload?.vendor_id) throw new Error("Please select vendor");

  const vendorSnap = await getDoc(doc(db, "vendors", payload.vendor_id));
  if (!vendorSnap.exists()) throw new Error("Vendor not found - refresh the page and pick the vendor again.");
  const vendor = vendorSnap.data();
  if (vendor.company_id !== companyId) throw new Error("Access denied - vendor of another company");

  const status  = vendorGstStatus(vendor);
  const checked = checkPOLines(payload.items, { vendorUnregistered: status === "unregistered" });
  if (!checked.ok) throw new Error(`Please fix these lines:\n- ${checked.errors.join("\n- ")}`);
  if (checked.lines.filter((l) => !l._cancelled).length === 0) throw new Error("Add at least one item.");

  return {
    ...payload,
    vendor_name:           payload.vendor_name || vendor.vendor_name || vendor.name || "",
    vendor_email:          vendor.email   || payload.vendor_email   || "",
    vendor_phone:          vendor.phone   || payload.vendor_phone   || "",
    vendor_address:        vendor.address || payload.vendor_address || "",
    vendor_gst:            vendorGstinOf(vendor),
    vendor_gst_registered: gstRegisteredFrom(status),
    items:                 checked.lines,
  };
};

export const createPurchaseOrder =
  async (payload) => {

    try {

      // GENERATE PO NUMBER

      // D-291 P3c: vendor + lines checked BEFORE the PO number is reserved.
      const prepared =
        await preparePOPayload(payload);

      const poNumber =
        await generatePONumber(payload.company_id);

      const purchaseOrderDoc =
        buildPurchaseOrderDoc(prepared, poNumber);

      // CREATE PURCHASE ORDER

      const purchaseOrderRef =
        await addDoc(

          collection(
            db,
            "purchase_orders"
          ),

          purchaseOrderDoc
        );

      // UPDATE SOURCE REQUEST

      if (
        payload
          .source_purchase_request_id
      ) {

        await updateDoc(

          doc(
            db,
            "purchase_requests",
            payload.source_purchase_request_id
          ),

          {

            po_created: true,

            po_number: poNumber,

            po_id:
              purchaseOrderRef.id,

            status: "po_created",

            updated_at:
              serverTimestamp()

          }
        );
      }

      console.log(
        "PURCHASE ORDER CREATED",
        purchaseOrderRef.id
      );

      return {

        success: true,

        po_id:
          purchaseOrderRef.id,

        po_number: poNumber,

        data: purchaseOrderDoc

      };

    } catch (error) {

      console.error(

        "Purchase order creation failed",

        error

      );

      return {

        success: false,

        error

      };
    }
  };

/*

  CREATE PURCHASE ORDER FROM A PURCHASE REQUEST (D-304 B)

  WHY: the Assign Vendor screen used to create the PO, and only afterwards
  update the PR, in separate writes with no check — a double click or a
  second tab made TWO POs from one PR. Now the PO and the PR update happen
  in ONE transaction that refuses if the PR already has a PO or was
  cancelled. The PO number is reserved just before (a refused attempt can
  leave a gap in PO numbers — never a duplicate PO).

*/
export const createPurchaseOrderFromRequest = async (payload) => {
  const prId      = String(payload?.source_purchase_request_id || "").trim();
  const companyId = String(payload?.company_id || "").trim();
  if (!prId)      throw new Error("Purchase request missing");
  if (!companyId) throw new Error("company_id required");
  if (!payload.vendor_id) throw new Error("Please select vendor");
  if (!payload.created_by) throw new Error("Your login could not be read. Please log in again.");

  const prRef = doc(db, "purchase_requests", prId);

  // Quick check first, so a PR that is already converted does not use up
  // a PO number. Re-checked inside the transaction.
  const pre = await getDoc(prRef);
  if (!pre.exists()) throw new Error("Purchase request not found");
  const preData = pre.data();
  if (preData.company_id !== companyId) throw new Error("Access denied — purchase request of another company");
  if (preData.po_id) throw new Error(`PO ${preData.po_number || preData.po_id} already exists for this request.`);
  if (preData.status === "cancelled" || preData.procurement_status === "cancelled") {
    throw new Error("This purchase request was cancelled.");
  }

  // D-291 P3c: vendor + lines checked BEFORE the PO number is reserved.
  const prepared = await preparePOPayload(payload);

  const poNumber = await generatePONumber(companyId);
  const poRef    = doc(collection(db, "purchase_orders"));
  const poDoc    = buildPurchaseOrderDoc(
    { ...prepared, source_type: "procurement_queue" },
    poNumber
  );

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(prRef);
    if (!snap.exists()) throw new Error("Purchase request not found");
    const pr = snap.data();
    if (pr.company_id !== companyId) throw new Error("Access denied — purchase request of another company");
    if (pr.po_id || pr.po_created || pr.status === "po_created" || pr.procurement_status === "po_generated") {
      throw new Error(`A PO (${pr.po_number || pr.po_id}) was already created for this request — nothing was saved.`);
    }
    if (pr.status === "cancelled" || pr.procurement_status === "cancelled") {
      throw new Error("This purchase request was cancelled — nothing was saved.");
    }

    transaction.set(poRef, poDoc);
    transaction.update(prRef, {
      po_created:         true,
      po_id:              poRef.id,
      po_number:          poNumber,
      status:             "po_created",
      procurement_status: "po_generated",
      vendor_id:          payload.vendor_id || "",
      vendor_name:        payload.vendor_name || "",
      vendor_assigned_at: serverTimestamp(),
      updated_at:         serverTimestamp(),
    });
  });

  return { success: true, po_id: poRef.id, po_number: poNumber, data: poDoc };
};

/*

  AMEND / APPROVE / MARK AS ORDERED (D-291 P3c)

  WHY: these three used to be written from the PO Detail page's old copy of
  the PO (a getDoc check, then a separate updateDoc). Two people could
  overwrite each other, and an approver could approve a value that someone
  amended after the approver looked at it. Amend also overwrote base_rate
  with rate, dropped discount / GST / HSN edits and kept the OLD item's code
  when the item was switched. Now each is ONE transaction that re-reads the
  PO; Amend and Approve refuse if the PO changed since the page loaded it
  (updated_at); lines are merged by line_id (D-295 - never rebuilt) and
  checked by checkPOLines(); amending an APPROVED PO sends it back to Draft.

*/

// Who may approve / mark ordered / amend: PO_APPROVER_ROLES / PO_AMEND_ROLES
// from src/utils/poRoles.js (HIGH-01 - same lists as the PO Detail screen).
// Amend is allowed only before the PO is sent to the vendor.
const PO_AMENDABLE_STATUSES = ["draft", "approved"];

// Same Firestore Timestamp? (seconds + nanoseconds; both missing = same)
const sameStamp = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Number(a.seconds) === Number(b.seconds) &&
    Number(a.nanoseconds) === Number(b.nanoseconds);
};

const CHANGED_MESSAGE =
  "This PO was changed by someone else after you opened it - nothing was saved. Refresh the page and try again.";

const userName = (user) => user?.name || user?.displayName || user?.email || "Unknown";

// The vendor's registration status, read inside the transaction (the vendor
// may have been marked Unregistered after the PO was made).
const vendorStatusInTx = async (transaction, po) => {
  if (!po.vendor_id) return "not_entered";
  const vSnap = await transaction.get(doc(db, "vendors", po.vendor_id));
  if (!vSnap.exists() || vSnap.data().company_id !== po.company_id) return "not_entered";
  return vendorGstStatus(vSnap.data());
};

// Only the fields a person may change in Amend, copied when present.
const AMEND_FIELDS = [
  "item_name", "item_code", "item_category", "unit", "ordered_qty",
  "base_rate", "rate_not_agreed", "discount_type", "discount_value",
  "gst_percent", "hsn_code",
];
const pickAmendFields = (changed, itemId) => {
  const out = { item_id: itemId, is_custom_item: String(itemId).startsWith("custom_") };
  for (const key of AMEND_FIELDS) {
    if (changed[key] !== undefined) out[key] = changed[key];
  }
  out._cancelled = changed._cancelled === true;
  return out;
};

/**
 * @param {Object}   args
 * @param {string}   args.po_id
 * @param {string}   args.company_id
 * @param {Object}   args.loaded_updated_at  po.updated_at as the page loaded it
 * @param {Array}    args.lines   the screen's full line list. Saved lines carry
 *                   their line_id (or _saved_index for old lines without one);
 *                   new lines carry neither. A saved line not sent is kept.
 * @param {string}   args.reason  mandatory
 * @param {Object}   args.user    { uid, name, email }
 * @returns {{ back_to_draft: boolean, po_number: string }}
 */
export const amendPurchaseOrder = async ({ po_id, company_id, loaded_updated_at, lines, reason, user }) => {
  if (!po_id)      throw new Error("Purchase order missing");
  if (!company_id) throw new Error("company_id required");
  if (!user?.uid)  throw new Error("Your login could not be read. Please log in again.");
  if (!hasPoRole(PO_AMEND_ROLES, user.role)) throw new Error("Only Owner / SM / Manager can amend a PO.");
  if (!String(reason || "").trim()) throw new Error("Amendment reason is mandatory.");

  const poRef = doc(db, "purchase_orders", po_id);
  let result = { back_to_draft: false, po_number: "" };

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(poRef);
    if (!snap.exists()) throw new Error("Purchase order not found");
    const po = snap.data();
    if (po.company_id !== company_id) throw new Error("Access denied - purchase order of another company");
    if (po.grn_locked === true || !PO_AMENDABLE_STATUSES.includes(po.po_status)) {
      throw new Error("This PO can no longer be amended - it is already ordered, received or closed.");
    }
    if (!sameStamp(po.updated_at, loaded_updated_at)) throw new Error(CHANGED_MESSAGE);
    const vendorStatus = await vendorStatusInTx(transaction, po);

    const now      = Date.now();
    const saved    = Array.isArray(po.items) ? po.items : [];
    const incoming = Array.isArray(lines) ? lines : [];
    const used     = new Set();

    // Saved lines first, in their saved order - each merged with its edit.
    const merged = saved.map((s, idx) => {
      const k = incoming.findIndex((l, j) => !used.has(j) && (
        (s.line_id && l.line_id === s.line_id) ||
        (!s.line_id && l._saved_index === idx)
      ));
      const lineId = s.line_id || `${String(s.item_id || "line").trim()}_${idx}`;
      if (k < 0) return { ...s, line_id: lineId };
      used.add(k);
      const c = incoming[k];
      const itemId = String(c.item_id || "").trim() || `custom_${now}_${idx}`;
      return { ...s, ...pickAmendFields(c, itemId), line_id: lineId };
    });

    // New lines (added in Amend) after them. A new line cancelled before the
    // save never existed on the PO -> not added.
    incoming.forEach((c, j) => {
      if (used.has(j) || c._cancelled === true) return;
      const itemId = String(c.item_id || "").trim() || `custom_${now}_n${j}`;
      merged.push({
        line_id:             `${itemId}_a${now}_${j}`,
        received_qty:        0,
        rejected_qty:        0,
        item_receive_status: "pending",
        item_status:         "pending",
        amount:              0,
        sources:             [],
        ...pickAmendFields(c, itemId),
      });
    });

    const checked = checkPOLines(merged, { vendorUnregistered: vendorStatus === "unregistered" });
    if (!checked.ok) throw new Error(`Please fix these lines:\n- ${checked.errors.join("\n- ")}`);
    const active = checked.lines.filter((l) => !l._cancelled);
    if (active.length === 0) throw new Error("At least one active item is required.");

    const withPending = checked.lines.map((l) => (l._cancelled ? l : {
      ...l,
      pending_qty: Math.max(Number(l.ordered_qty || 0) - Number(l.received_qty || 0), 0),
    }));

    // Landed cost on ACTIVE lines only, so a cancelled line takes no freight share.
    const activeLanded = calculateLandedCost(withPending.filter((l) => !l._cancelled), po.freight_amount);
    let k = 0;
    const finalItems = withPending.map((l) => (l._cancelled ? l : activeLanded[k++]));
    const landedGrandTotal = activeLanded.reduce((s, l) => s + (l.landed_total || 0), 0);
    // grand_total stays the RECEIVED value (nothing is received before "ordered").
    const grandTotal = finalItems.reduce(
      (s, l) => (l._cancelled ? s : s + Number(l.received_qty || 0) * Number(l.rate || 0)), 0
    );
    const value = computeOrderValue(finalItems, null);
    const backToDraft = po.po_status === "approved";

    transaction.update(poRef, {
      items:              finalItems,
      grand_total:        Number(grandTotal.toFixed(2)),
      landed_grand_total: Number(landedGrandTotal.toFixed(2)),
      amendment_log: arrayUnion({
        amended_at:          new Date().toISOString(),
        amended_by:          user.uid,
        amended_by_name:     userName(user),
        reason:              String(reason).trim(),
        status_before:       po.po_status,
        items_snapshot:      finalItems,
        grand_total_after:   Number(grandTotal.toFixed(2)),
        ordered_value_after: value.total,
      }),
      ...(backToDraft ? {
        po_status:        "draft",
        approved_by:      "",
        approved_by_name: "",
        approved_at:      null,
        approved_value:   null,
      } : {}),
      updated_at:      serverTimestamp(),
      updated_by:      user.uid,
      updated_by_name: userName(user),
    });

    result = { back_to_draft: backToDraft, po_number: po.po_number || "" };
  });

  return result;
};

/**
 * Draft -> Approved. Saves approved_value, the ordered value the approver saw.
 * Refuses what Amend refuses: every line goes through checkPOLines() (the
 * release-prep audit, 1 Nov: computeOrderValue() alone let an old Draft with
 * no rate and no GST be approved). The lines are only checked, never written.
 * @returns {{ po_number: string, approved_value: Object }}
 */
export const approvePurchaseOrder = async ({ po_id, company_id, loaded_updated_at, user }) => {
  if (!po_id)      throw new Error("Purchase order missing");
  if (!company_id) throw new Error("company_id required");
  if (!user?.uid)  throw new Error("Your login could not be read. Please log in again.");
  if (!hasPoRole(PO_APPROVER_ROLES, user.role)) throw new Error("You don't have permission to approve this PO.");

  const poRef = doc(db, "purchase_orders", po_id);
  let result = { po_number: "", approved_value: null };

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(poRef);
    if (!snap.exists()) throw new Error("Purchase order not found");
    const po = snap.data();
    if (po.company_id !== company_id) throw new Error("Access denied - purchase order of another company");
    if (po.po_status !== "draft") throw new Error("PO status has changed. Please refresh.");
    if (!sameStamp(po.updated_at, loaded_updated_at)) throw new Error(CHANGED_MESSAGE);
    const vendorStatus = await vendorStatusInTx(transaction, po);

    const active = (po.items || []).filter((l) => !l._cancelled);
    if (active.length === 0) throw new Error("This PO has no active items.");
    if (vendorStatus === "unregistered" && active.some((l) => Number(l.gst_percent || 0) > 0)) {
      throw new Error("This vendor is unregistered, but some lines carry GST. Amend the PO (GST 0) before approving.");
    }
    const lineCheck = checkPOLines(po.items, { vendorUnregistered: vendorStatus === "unregistered" });
    if (!lineCheck.ok) throw new Error(`Fix these lines with Amend before approving:\n- ${lineCheck.errors.join("\n- ")}`);
    const value = computeOrderValue(po.items, null);
    if (!value.ok) throw new Error(`Please amend these lines before approving:\n- ${value.errors.join("\n- ")}`);

    const approvedValue = {
      taxable_total:         value.taxable_total,
      tax_total:             value.tax_total,
      total:                 value.total,
      lines_rate_not_agreed: value.rate_not_agreed.length,
      lines_rate_missing:    value.rate_missing.length,
    };

    transaction.update(poRef, {
      po_status:        "approved",
      approved_by:      user.uid,
      approved_by_name: userName(user),
      approved_at:      serverTimestamp(),
      approved_value:   approvedValue,
      updated_at:       serverTimestamp(),
      updated_by:       user.uid,
    });

    result = { po_number: po.po_number || "", approved_value: approvedValue };
  });

  return result;
};

/**
 * Approved -> Ordered (the PO was sent to the vendor). GRN is possible after this.
 * @returns {{ po_number: string }}
 */
export const markPurchaseOrderOrdered = async ({ po_id, company_id, user }) => {
  if (!po_id)      throw new Error("Purchase order missing");
  if (!company_id) throw new Error("company_id required");
  if (!user?.uid)  throw new Error("Your login could not be read. Please log in again.");
  if (!hasPoRole(PO_APPROVER_ROLES, user.role)) throw new Error("Only SM / Owner / Manager can mark a PO as ordered.");

  const poRef = doc(db, "purchase_orders", po_id);
  let result = { po_number: "" };

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(poRef);
    if (!snap.exists()) throw new Error("Purchase order not found");
    const po = snap.data();
    if (po.company_id !== company_id) throw new Error("Access denied - purchase order of another company");
    if (po.po_status !== "approved") throw new Error("PO status has changed. Please refresh.");

    transaction.update(poRef, {
      po_status:       "ordered",
      ordered_by:      user.uid,
      ordered_by_name: userName(user),
      ordered_at:      serverTimestamp(),
      updated_at:      serverTimestamp(),
      updated_by:      user.uid,
    });

    result = { po_number: po.po_number || "" };
  });

  return result;
};

/*

  REASSIGN SITE ENGINEER(S) ON A DIRECT SITE PO (HIGH-01)

  WHY: the old reassignSiteEngineers() (directSitePurchaseService.js) had no
  role check, no transaction and wrote no updated_by - anyone who could open
  the PO could re-pin it, from the page's old copy. Now: Owner / SM / Manager
  only, ONE transaction that re-reads the PO, refused if the PO changed since
  the page loaded it (updated_at), and only while the Site Engineer has not
  confirmed yet. Writes exactly the 4 fields the rules allow.
  An empty list is valid: anyone assigned to the project can confirm.

*/

// Same cap as the rule (assigned_site_engineer_ids.size() <= 30).
const MAX_PINNED_SITE_ENGINEERS = 30;

/**
 * @param {Object}   args
 * @param {string}   args.po_id
 * @param {string}   args.company_id
 * @param {Object}   args.loaded_updated_at  po.updated_at as the page loaded it
 * @param {string[]} args.assigned_site_engineer_ids  users doc ids; may be empty
 * @param {Object}   args.user    { uid, role, name, email }
 * @returns {{ po_number: string }}
 */
export const reassignPOSiteEngineers = async ({
  po_id, company_id, loaded_updated_at, assigned_site_engineer_ids, user,
}) => {
  if (!po_id)      throw new Error("Purchase order missing");
  if (!company_id) throw new Error("company_id required");
  if (!user?.uid)  throw new Error("Your login could not be read. Please log in again.");
  if (!hasPoRole(PO_REASSIGN_ROLES, user.role)) {
    throw new Error("Only Owner / SM / Manager can change who confirms this PO.");
  }
  const ids = assigned_site_engineer_ids;
  if (!Array.isArray(ids) || ids.some((id) => typeof id !== "string" || id.trim() === "")) {
    throw new Error("The Site Engineer list could not be read. Refresh the page and try again.");
  }
  if (ids.length > MAX_PINNED_SITE_ENGINEERS) {
    throw new Error(`Pin at most ${MAX_PINNED_SITE_ENGINEERS} Site Engineers to one PO.`);
  }

  const poRef = doc(db, "purchase_orders", po_id);
  let result = { po_number: "" };

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(poRef);
    if (!snap.exists()) throw new Error("Purchase order not found");
    const po = snap.data();
    if (po.company_id !== company_id) throw new Error("Access denied - purchase order of another company");
    if (po.purchase_mode !== "direct_site_purchase") throw new Error("This PO is not a Direct Site Purchase.");
    if (po.grn_locked === true || po.site_confirmation_status !== "pending_site_confirmation") {
      throw new Error("Cannot change who confirms - the site has already confirmed or this PO is finalized.");
    }
    if (!sameStamp(po.updated_at, loaded_updated_at)) throw new Error(CHANGED_MESSAGE);

    transaction.update(poRef, {
      assigned_site_engineer_ids: ids,
      updated_at:      serverTimestamp(),
      updated_by:      user.uid,
      updated_by_name: userName(user),
    });

    result = { po_number: po.po_number || "" };
  });

  return result;
};